import { AdminEventModel } from 'types/admin/trackmanEventsAdmin';

export const sortByDate = <T>(
  itemsArray: T[],
  getProperty: (item: T) => string | number | Date,
  order: 'ASCENDING' | 'DESCENDING'
): T[] => {
  return itemsArray.sort((a, b) => {
    const dateA = new Date(getProperty(a)).getTime();
    const dateB = new Date(getProperty(b)).getTime();
    return order === 'ASCENDING' ? dateA - dateB : dateB - dateA;
  });
};

export const sortByString = (itemsArray: Array<any>, sortByProperty: string, order: string): Array<any> => {
  return itemsArray.sort((a, b) => {
    const stringA = a[sortByProperty].toLowerCase();
    const stringB = b[sortByProperty].toLowerCase();

    switch (order) {
      case 'ASCENDING':
        return stringA.localeCompare(stringB);
      case 'DESCENDING':
        return stringB.localeCompare(stringA);
      default:
        return 0;
    }
  });
};

export const sortByBoolean = (itemsArray: Array<any>, sortByProperty: string, order: string): Array<any> => {
  return itemsArray.sort((a, b) => {
    const booleanA = a[sortByProperty] ? 1 : 0;
    const booleanB = b[sortByProperty] ? 1 : 0;

    switch (order) {
      case 'ASCENDING':
        return booleanA - booleanB;
      case 'DESCENDING':
        return booleanB - booleanA;
      default:
        return 0;
    }
  });
};

const BOOLEAN_PROPS = ['premiumEvent', 'visible', 'certification', 'comingSoon'] as const;
const DATE_PROPS = ['createdAt', 'updatedAt'] as const;
const STRING_PROPS = ['title', 'kind', 'primaryCategory'] as const;

type BooleanProp = (typeof BOOLEAN_PROPS)[number];
type DateProp = (typeof DATE_PROPS)[number];
type StringProp = (typeof STRING_PROPS)[number];
type SortablePropTypes = BooleanProp | DateProp | StringProp | 'state';
export type ValidSortProp<T> = Extract<SortablePropTypes, keyof T>;
export type AdminEventSortKeys = ValidSortProp<AdminEventModel> | 'state';

export const dataSorter = <T extends Record<string, any>>(
  itemsList: T[],
  sortByProperty: ValidSortProp<T>,
  sortOrder: 'ASCENDING' | 'DESCENDING'
): T[] => {
  if (STRING_PROPS.includes(sortByProperty as StringProp)) {
    return sortByString(itemsList, sortByProperty, sortOrder);
  }

  if (BOOLEAN_PROPS.includes(sortByProperty as BooleanProp)) {
    return sortByBoolean(itemsList, sortByProperty, sortOrder);
  }

  if (DATE_PROPS.includes(sortByProperty as DateProp)) {
    return sortByDate(itemsList, (item) => item[sortByProperty], sortOrder);
  }

  return itemsList;
};

export const eventDataSorter = (
  itemsList: AdminEventModel[],
  sortByProperty: AdminEventSortKeys,
  sortOrder: 'ASCENDING' | 'DESCENDING'
): AdminEventModel[] => {
  if (sortByProperty === 'state') {
    const getState = (item: AdminEventModel): string => {
      if (item.recordingReady) return 'On demand';
      if (!item.recordingReady && item.locationType === 'Online' && new Date(item.startDate) < new Date()) return 'Missing recording';
      if (!item.recordingReady && new Date(item.startDate) < new Date()) return 'Event has passed';
      return 'Upcoming';
    };

    return [...itemsList].sort((a, b) => {
      const stateA = getState(a);
      const stateB = getState(b);
      return sortOrder === 'ASCENDING' ? stateA.localeCompare(stateB) : stateB.localeCompare(stateA);
    });
  }

  return dataSorter(itemsList, sortByProperty as ValidSortProp<AdminEventModel>, sortOrder);
};
